import { toRaw } from 'vue';
import type { ServicePackage } from '@/types';

// A list of package lines (appointment parts, order items) is rendered as rows: one per plain
// line, or one per package selection (all lines that came from picking a package once, shown
// as a single card).
//
// A line's servicePackageServiceId can't tell two selections of the same package apart, since
// it points at the package template. So every selection gets its own key, stored per line
// object (keyed by toRaw(), because stores wrap the lines in reactive proxies). Lines loaded
// from the backend have no key and are grouped by package instead, starting a new group
// whenever one of the package's services comes around again.

export type PackageLine = { servicePackageServiceId?: number | null };

export type PackageRow =
  | { key: string; kind: 'single'; indexes: [number] }
  | {
      key: string;
      kind: 'package';
      indexes: number[];
      servicePackage: ServicePackage;
    };

const selectionKeysByLine = new WeakMap<PackageLine, string>();
let nextSelectionKey = 0;

// Called once per package selection, with the lines it created
export const assignPackageSelectionKey = (lines: PackageLine[]) => {
  const key = `selection-${nextSelectionKey++}`;
  lines.forEach((line) => {
    selectionKeysByLine.set(toRaw(line), key);
  });
};

// Groups contiguous lines of the same package selection into one row
export const groupPackageRows = <Line extends PackageLine>(
  lines: Line[],
  packageForLine: (line: Line) => ServicePackage | undefined,
): PackageRow[] => {
  const groupOf = (line: Line) => {
    const servicePackage = packageForLine(line);
    if (!servicePackage) {
      return undefined;
    }
    return {
      servicePackage,
      selectionKey:
        selectionKeysByLine.get(toRaw(line)) ?? `template-${servicePackage.id}`,
    };
  };

  const rows: PackageRow[] = [];
  let index = 0;

  while (index < lines.length) {
    const group = groupOf(lines[index]);

    if (!group) {
      rows.push({ key: `single-${index}`, kind: 'single', indexes: [index] });
      index += 1;
      continue;
    }

    const indexes = [index];
    const servicePackageServiceIds = new Set([
      lines[index].servicePackageServiceId,
    ]);
    let next = index + 1;

    while (
      next < lines.length &&
      groupOf(lines[next])?.selectionKey === group.selectionKey &&
      !servicePackageServiceIds.has(lines[next].servicePackageServiceId)
    ) {
      servicePackageServiceIds.add(lines[next].servicePackageServiceId);
      indexes.push(next);
      next += 1;
    }

    rows.push({
      key: `package-${index}`,
      kind: 'package',
      indexes,
      servicePackage: group.servicePackage,
    });
    index = next;
  }

  return rows;
};
